import Typography from '@material-ui/core/Typography'
import React, { useEffect, useState } from 'react'
import { useSwipeable } from 'react-swipeable'

import { IAsset } from '../../utils/types'
import { TLink } from '../Link'
import TrainStop from '../TrainStop'
import TrainstopImageSlide from '../TrainstopImageSlide'
import Wrapper from '../UI/Wrapper'
import {
  ControlContainer,
  HeadlineContainer,
  HeroTrainstopHeadline,
  TrainstopSectionContainer,
} from './styles'

interface TrainstopSectionProps {
  headline: string
  ctaText?: string
  isHeroTrainStop?: 'Yes' | 'No' | undefined
  slides: readonly {
    id: string
    headline?: string | null
    body?: string | null
    nav_label: string | null
    impact_statistic?: string | null
    feature_image?: IAsset | null
    background_image?: IAsset | null
    background_image_mobile?: IAsset | null
    theme: string | null
    cta:
      | readonly ({
          link: readonly TLink[] | null
          text: string | null
        } | null)[]
      | null
  }[]
  className?: string
  isTech?: boolean
}

export type TTrainStopSlide = TrainstopSectionProps['slides'][number]

const themeIsDarkOrLight = (
  theme: unknown | 'Dark' | 'Light',
): theme is 'Dark' | 'Light' =>
  (theme as 'Dark' | 'Light') === 'Dark' ||
  (theme as 'Dark' | 'Light') === 'Light'

const TrainstopSection = ({
  headline,
  slides,
  ctaText,
  isHeroTrainStop,
  className = '',
  isTech,
}: TrainstopSectionProps) => {
  const [activeSlide, setActiveSlide] = useState(-1)
  const slidesToMap = isHeroTrainStop === 'Yes' ? slides.slice(1) : slides
  const slideTheme = slides?.[activeSlide]?.theme

  useEffect(() => {
    if (isHeroTrainStop !== 'Yes') {
      setActiveSlide(0)
    }
  }, [])

  const swipeHandlers = useSwipeable({
    onSwipedRight: () => {
      if (activeSlide > 0) {
        setActiveSlide(activeSlide - 1)
      }
    },
    onSwipedLeft: () => {
      if (activeSlide < slidesToMap.length - 1) {
        setActiveSlide(activeSlide + 1)
      }
    },
  })

  return (
    <TrainstopSectionContainer
      className={`${className} ${
        activeSlide === -1
          ? slides?.[0]?.theme?.toLowerCase()
          : slidesToMap?.[activeSlide]?.theme?.toLowerCase()
      } ${slides?.[0]?.nav_label === 'Peer Insights' ? 'impactSlides' : ''}`}
    >
      <div className="slidesContainer" {...swipeHandlers}>
        {isHeroTrainStop === 'Yes' ? (
          <TrainstopImageSlide
            activeSlide={activeSlide}
            ctaText={ctaText}
            initialSlide={true}
            isActive={activeSlide === -1}
            isHeroTrainStop={isHeroTrainStop}
            slide={slides[0]}
          />
        ) : null}
        {slidesToMap.map((slide, index) => (
          <TrainstopImageSlide
            activeSlide={activeSlide}
            className={className}
            isActive={index === activeSlide}
            isHeroTrainStop={isHeroTrainStop}
            key={slide.id}
            slide={slide}
          />
        ))}
      </div>
      {isHeroTrainStop === 'Yes' ? (
        <HeroTrainstopHeadline>
          <Wrapper>
            <Typography
              className="heroTrainstopSubhead"
              component="h2"
              variant="h2"
            >
              {headline}
            </Typography>
          </Wrapper>
        </HeroTrainstopHeadline>
      ) : (
        <HeadlineContainer>
          <Wrapper>
            <Typography className="subhead" component="h2" variant="h4">
              {headline}
            </Typography>
          </Wrapper>
        </HeadlineContainer>
      )}
      <ControlContainer>
        <Wrapper>
          <TrainStop
            activeSlide={activeSlide}
            isTech={isTech}
            setActiveSlide={setActiveSlide}
            slides={slidesToMap}
            theme={themeIsDarkOrLight(slideTheme) ? slideTheme : 'Light'}
          />
        </Wrapper>
      </ControlContainer>
    </TrainstopSectionContainer>
  )
}

export default TrainstopSection
